/**
 * SellnBuy / SocialPulse Local — landing preview (Phase 1.3 stub).
 *
 * Purpose right now: give stakeholders a visual to react to while the
 * universal +POST wizard, listing detail, and per-country admin are
 * still being built.
 *
 * What it does today:
 *   • Fetches the 13 seeded country divisions and lets the user switch
 *     which country they're browsing.
 *   • Fetches the 22 top-level categories and renders them as a grid.
 *   • Renders the "listing intent" chips (Sell / Free / Wanted / Swap
 *     / Services / Tasks / Volunteer / Food) that will drive the +POST
 *     wizard flow.
 *   • Shows a live listings row (empty state until we seed sample data
 *     or users start posting).
 *
 * Deliberately restrained styling so it inherits the site theme and
 * doesn't lock us into a look before Phase 1.4 design.
 */
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, MapPin, Plus, ArrowRight, ShoppingBag, Gift, Wrench,
  HandHelping, Utensils, Sparkles,
} from "lucide-react";
import axios from "axios";
import PostWizard from "@/components/local/PostWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/local`;

const INTENTS = [
  { key: "sell",      label: "Sell", icon: ShoppingBag, tone: "#1F6FEB" },
  { key: "free",      label: "Give free", icon: Gift, tone: "#0E7C55" },
  { key: "wanted",    label: "Wanted", icon: Search, tone: "#B85C00" },
  { key: "swap",      label: "Swap", icon: ArrowRight, tone: "#7A46C4" },
  { key: "service_offer",   label: "Offer service", icon: Wrench, tone: "#0E7C55" },
  { key: "service_request", label: "Need service", icon: Wrench, tone: "#B85C00" },
  { key: "task_request",    label: "Post a task", icon: Sparkles, tone: "#7A46C4" },
  { key: "volunteer_opportunity", label: "Volunteer", icon: HandHelping, tone: "#0E7C55" },
  { key: "food_free",       label: "Share food", icon: Utensils, tone: "#0E7C55" },
];


export default function SellnBuyHome() {
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState("US");
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState(null);

  const country = useMemo(
    () => countries.find((c) => c.code === countryCode) || null,
    [countries, countryCode],
  );

  useEffect(() => {
    (async () => {
      try {
        const [c, cat] = await Promise.all([
          axios.get(`${API}/countries?active_only=true`),
          axios.get(`${API}/categories`),
        ]);
        setCountries(c.data.items || []);
        setCategories(cat.data.items || []);
      } catch (e) {
        // Non-fatal — UI still renders.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!countryCode) return;
    axios
      .get(`${API}/listings?country_code=${countryCode}&limit=12`)
      .then((r) => setListings(r.data.items || []))
      .catch(() => setListings([]));
  }, [countryCode]);

  return (
    <div
      data-testid="sellnbuy-home"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet>
        <title>SellnBuy · SocialPulse Local — Buy · Sell · Share · Hire · Help</title>
        <meta
          name="description"
          content="SellnBuy · SocialPulse Local: your hyperlocal marketplace for goods, services, tasks, volunteering and food. Buy · Sell · Share · Hire · Help · Volunteer · Rescue."
        />
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden border-b"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            background:
              "radial-gradient(circle at 10% 10%, #0E7C55 0%, transparent 40%), radial-gradient(circle at 90% 20%, #1F6FEB 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-14">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-6 text-[11px] font-mono uppercase tracking-[0.18em]"
            style={{
              background: "rgba(14,124,85,0.08)",
              color: "#0E7C55",
              border: "1px solid rgba(14,124,85,0.2)",
            }}
          >
            <span>SocialPulse Local · preview</span>
          </div>
          <h1
            data-testid="hero-title"
            className="font-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.03] tracking-tight max-w-3xl"
            style={{ color: "var(--wofy-text)" }}
          >
            Buy · Sell · Share · Hire ·{" "}
            <em style={{ color: "#0E7C55", fontStyle: "italic" }}>Help</em>
          </h1>
          <p
            className="mt-5 text-lg md:text-xl max-w-2xl leading-[1.5]"
            style={{ color: "var(--wofy-text-soft)" }}
          >
            One place to trade, help and take care of your neighbourhood.
            Commerce when money makes sense — community when it doesn't.
          </p>

          {/* Search + country selector */}
          <div
            data-testid="hero-search"
            className="mt-9 flex flex-col sm:flex-row items-stretch gap-2 max-w-3xl"
          >
            <div
              className="flex-1 flex items-center gap-2 rounded-md px-4 py-3 border bg-white"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <Search className="w-4 h-4 opacity-60" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What are you looking for? (e.g. dining table, plumber, free tomatoes)"
                className="flex-1 outline-none bg-transparent text-[15px]"
                data-testid="hero-search-input"
              />
            </div>
            <div
              className="flex items-center gap-2 rounded-md px-4 py-3 border bg-white min-w-[200px]"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <MapPin className="w-4 h-4 opacity-60" />
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="flex-1 outline-none bg-transparent text-[15px]"
                data-testid="hero-country-select"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} · {c.currency}
                  </option>
                ))}
              </select>
            </div>
            <button
              data-testid="hero-post-button"
              className="wofy-btn-primary flex items-center gap-2 justify-center px-6"
              onClick={() => { setWizardType(null); setWizardOpen(true); }}
            >
              <Plus className="w-4 h-4" /> POST
            </button>
          </div>

          {/* Intent chips */}
          <div
            data-testid="intent-chips"
            className="mt-7 flex flex-wrap gap-2"
          >
            {INTENTS.map(({ key, label, icon: Icon, tone }) => (
              <button
                key={key}
                data-testid={`intent-${key}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] transition-all hover:-translate-y-0.5 border"
                style={{
                  borderColor: "var(--wofy-border)",
                  background: "white",
                  color: "var(--wofy-text)",
                }}
                onClick={() => { setWizardType(key); setWizardOpen(true); }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Country context bar ─────────────────────────────────── */}
      {country && (
        <section
          className="border-b"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        >
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-[13px]">
            <span style={{ color: "var(--wofy-text-soft)" }}>Now browsing</span>
            <span
              data-testid="country-badge"
              className="font-mono uppercase tracking-[0.15em] font-semibold"
              style={{ color: "var(--wofy-text)" }}
            >
              🇨🇴&nbsp;{country.name} · {country.currency}
              &nbsp;·&nbsp;{country.distance_unit}
              &nbsp;·&nbsp;fee&nbsp;{(country.platform_fee_bps / 100).toFixed(1)}%
              &nbsp;·&nbsp;pay via {country.payment_provider}
            </span>
          </div>
        </section>
      )}

      {/* ── Categories grid ─────────────────────────────────────── */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-baseline justify-between mb-8">
          <h2
            className="font-display text-2xl md:text-3xl"
            style={{ color: "var(--wofy-text)" }}
          >
            Browse by category
          </h2>
          <span
            className="text-[11px] font-mono uppercase tracking-[0.18em] opacity-60"
          >
            {categories.length} categories · seeded live
          </span>
        </div>
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : (
          <div
            data-testid="categories-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            {categories.map((c) => (
              <button
                key={c.slug}
                data-testid={`category-${c.slug}`}
                className="group flex items-center gap-3 rounded-md px-4 py-4 border text-left transition-all hover:-translate-y-0.5 hover:shadow-sm"
                style={{
                  borderColor: "var(--wofy-border)",
                  background: "white",
                }}
                onClick={() => nav(`/local/browse/${c.slug}?country=${countryCode}`)}
              >
                <span className="text-2xl">{c.icon || "•"}</span>
                <span
                  className="text-[14px] font-medium"
                  style={{ color: "var(--wofy-text)" }}
                >
                  {c.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ── Live listings preview ────────────────────────────── */}
      <section
        className="border-t"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
          <div className="flex items-baseline justify-between mb-8">
            <h2
              className="font-display text-2xl md:text-3xl"
              style={{ color: "var(--wofy-text)" }}
            >
              Fresh listings · {country?.name}
            </h2>
            <span
              className="text-[11px] font-mono uppercase tracking-[0.18em] opacity-60"
            >
              {listings.length} live
            </span>
          </div>
          {listings.length === 0 ? (
            <div
              data-testid="empty-listings"
              className="rounded-md border border-dashed p-10 text-center"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <p
                className="text-[15px] mb-4"
                style={{ color: "var(--wofy-text-soft)" }}
              >
                No listings yet in {country?.name}. Be the first — post
                something you'd like to sell, give, swap or ask for.
              </p>
              <button
                onClick={() => { setWizardType(null); setWizardOpen(true); }}
                className="wofy-btn-ghost inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Post the first listing
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((l) => (
                <Link
                  key={l.id}
                  to={`/local/listings/${l.id}`}
                  data-testid={`listing-card-${l.id}`}
                  className="rounded-md border p-4 hover:-translate-y-0.5 transition"
                  style={{ borderColor: "var(--wofy-border)", background: "white" }}
                >
                  <div className="text-[11px] font-mono uppercase tracking-[0.15em] opacity-60 mb-2">
                    {l.listing_type} · {l.location?.city || l.location?.country_code}
                  </div>
                  <div
                    className="font-display text-lg leading-tight mb-1"
                    style={{ color: "var(--wofy-text)" }}
                  >
                    {l.title}
                  </div>
                  <div className="text-sm opacity-70 line-clamp-2">
                    {l.description}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Roadmap / phase status ─────────────────────────── */}
      <section
        className="border-t"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] mb-3 opacity-70">
            Build phase
          </div>
          <h2
            className="font-display text-2xl md:text-3xl mb-6"
            style={{ color: "var(--wofy-text)" }}
          >
            Where we are on the SocialPulse Local plan
          </h2>
          <ul className="grid gap-3 md:grid-cols-2 text-[14px] leading-[1.6]">
            <li>
              ✅ <b>Phase 1.1 — Country Divisions</b>. 13 countries seeded
              with per-country currency, locale, distance-unit, payment
              provider and platform fee (%).
            </li>
            <li>
              ✅ <b>Phase 1.2 — Universal Listing Engine</b>. 30+ listing
              types, geospatial search, per-type expiry, risk scoring
              stub, DPDP-safe location coarsening.
            </li>
            <li>
              🚧 <b>Phase 1.3 — +POST wizard & listing detail</b>. AI
              intake, media upload, universal edit / cancel flows.
            </li>
            <li>
              🚧 <b>Phase 1.4 — Country Admin Console</b>. Per-country
              admins moderate their region and tune pricing / payouts.
            </li>
            <li>
              ⏭️ Phase 2 — Services & Tasks migration from
              sp_services_marketplace.
            </li>
            <li>
              ⏭️ Phase 3–6 — Community · FoodPulse · AI matching ·
              Cross-site distribution.
            </li>
          </ul>
        </div>
      </section>

      {/* Universal +POST wizard modal */}
      <PostWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        preselectType={wizardType}
        countryCode={countryCode}
      />
    </div>
  );
}
